import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Calendar, BookOpen, Calculator, ShieldCheck, Clock, Mail } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { Button } from "@/components/ui/button";
import { JsonLd } from "@/components/JsonLd";
import { breadcrumbSchema } from "@/lib/schemas";
import { buildSeoMeta } from "@/lib/seo";
import { z } from "zod";

const searchSchema = z.object({
  source: z
    .enum(["contact", "kalkulator", "lead-magnet", "exit-intent", "newsletter", "other"])
    .optional()
    .default("other"),
});

export const Route = createFileRoute("/dziekujemy")({
  validateSearch: searchSchema,
  head: () =>
    buildSeoMeta({
      title: "Dziękujemy | SyncTech",
      description: "Twoje zgłoszenie zostało wysłane. Odezwiemy się w ciągu 24 godzin.",
      path: "/dziekujemy",
      noindex: true,
    }),
  component: ThankYouPage,
});

const sourceCopy: Record<
  NonNullable<z.infer<typeof searchSchema>["source"]>,
  { title: string; subtitle: string }
> = {
  contact: {
    title: "Wiadomość wysłana",
    subtitle:
      "Dziękujemy za zgłoszenie. Odezwiemy się z konkretną propozycją w ciągu 24 godzin (dni robocze).",
  },
  kalkulator: {
    title: "Wycena w drodze",
    subtitle:
      "Szczegółowa rozpiska PDF dotrze na Twoją skrzynkę w ciągu 5 minut. Sprawdź też folder Spam.",
  },
  "lead-magnet": {
    title: "PDF gotowy do pobrania",
    subtitle:
      "Checklist 25 integracji powinien już być w folderze Pobrane. Wysłaliśmy też kopię na e-mail.",
  },
  "exit-intent": {
    title: "Kalkulator ROI w drodze",
    subtitle:
      "Plik z kalkulatorem ROI automatyzacji wysyłamy na Twojego maila w ciągu 5 minut.",
  },
  newsletter: {
    title: "Zapis potwierdzony",
    subtitle:
      "Witamy w gronie czytelników. Co miesiąc dostaniesz garść konkretów z e-commerce — bez spamu.",
  },
  other: {
    title: "Dziękujemy",
    subtitle: "Twoje zgłoszenie zostało odebrane. Odezwiemy się tak szybko, jak to możliwe.",
  },
};

function ThankYouPage() {
  const { source } = useSearch({ from: "/dziekujemy" });
  const copy = sourceCopy[(source ?? "other") as keyof typeof sourceCopy] ?? sourceCopy.other;

  // GA4 / Ads conversion event — odpali się gdy gtag jest dostępny (po cookie consent)
  useEffect(() => {
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: Array<Record<string, unknown>>;
    };
    if (typeof w.gtag === "function") {
      w.gtag("event", "generate_lead", {
        event_category: "conversion",
        event_label: source,
        source,
      });
    }
    // GTM dataLayer (na zapas — gdyby ktoś podpiął)
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event: "generate_lead", source });
    }
  }, [source]);

  const breadcrumbs = breadcrumbSchema([
    { name: "Strona główna", path: "/" },
    { name: "Dziękujemy", path: "/dziekujemy" },
  ]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <JsonLd data={breadcrumbs} />
      <Header />

      <main className="pt-32 pb-24">
        {/* Hero */}
        <section className="mx-auto max-w-3xl px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 180, damping: 16 }}
            className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/15 ring-8 ring-primary/5"
          >
            <CheckCircle2 className="h-10 w-10 text-primary" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-6 font-display text-4xl font-bold md:text-5xl"
          >
            {copy.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground"
          >
            {copy.subtitle}
          </motion.p>

          {source === "contact" && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mx-auto mt-8 grid max-w-2xl gap-3 text-left sm:grid-cols-2"
            >
              <div className="flex items-start gap-3 rounded-2xl border border-border/60 bg-card/50 p-4">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <div className="text-sm font-semibold">Odpowiedź w 24h</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Odezwę się osobiście w ciągu 24 godzin (dni robocze, pon.–pt., 9:00–17:00).
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-border/60 bg-card/50 p-4">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <div className="text-sm font-semibold">Potwierdzenie na e-mail</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Wysłałem kopię zgłoszenia na podany adres. Sprawdź też folder Spam / Oferty.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4 sm:col-span-2">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <div className="text-sm font-semibold">Zgoda RODO zarejestrowana</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Zapisałem treść i wersję udzielonej zgody wraz z datą i adresem IP — zgodnie z art. 7 RODO. Dane przetwarzam wyłącznie w celu udzielenia odpowiedzi. W każdej chwili możesz zażądać ich usunięcia pisząc na{" "}
                    <a href="mailto:kontakt@synctech.pl" className="font-medium text-primary hover:underline">
                      kontakt@synctech.pl
                    </a>
                    . Szczegóły:{" "}
                    <Link to="/polityka-prywatnosci" className="font-medium text-primary hover:underline">
                      Polityka prywatności
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </section>

        {/* Co dalej */}
        <section className="mx-auto mt-16 max-w-4xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="rounded-3xl border border-border/60 bg-card/50 p-8 backdrop-blur-sm"
          >
            <h2 className="font-display text-2xl font-bold">Co dalej?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Zanim się odezwiemy, sprawdź zasoby, które mogą Ci się przydać:
            </p>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <Link
                to="/blog"
                className="group rounded-2xl border border-border/40 bg-muted/10 p-5 transition hover:border-primary/40 hover:bg-primary/5"
              >
                <BookOpen className="h-6 w-6 text-primary" />
                <div className="mt-3 font-semibold">Blog</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Konkretne case studies i przewodniki z wdrożeń.
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:gap-2 transition-all">
                  Czytaj <ArrowRight className="h-3 w-3" />
                </span>
              </Link>

              <Link
                to="/kalkulator"
                className="group rounded-2xl border border-border/40 bg-muted/10 p-5 transition hover:border-primary/40 hover:bg-primary/5"
              >
                <Calculator className="h-6 w-6 text-primary" />
                <div className="mt-3 font-semibold">Kalkulator wyceny</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Sprawdź widełki kosztów wdrożenia w 2 minuty.
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:gap-2 transition-all">
                  Otwórz <ArrowRight className="h-3 w-3" />
                </span>
              </Link>

              <a
                href="https://calendly.com/synctech/15min"
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl border border-border/40 bg-muted/10 p-5 transition hover:border-primary/40 hover:bg-primary/5"
              >
                <Calendar className="h-6 w-6 text-primary" />
                <div className="mt-3 font-semibold">Konsultacja 15 min</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Wybierz wolny termin — bez zobowiązań, po polsku.
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:gap-2 transition-all">
                  Zarezerwuj <ArrowRight className="h-3 w-3" />
                </span>
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-border/40 pt-6">
              <p className="text-sm text-muted-foreground">
                Coś nie dotarło? Napisz na{" "}
                <a
                  href="mailto:kontakt@synctech.pl"
                  className="font-medium text-primary hover:underline"
                >
                  kontakt@synctech.pl
                </a>
                .
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="/">Wróć na stronę główną</Link>
              </Button>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
}
